import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../i18n';
import { PageHeader } from '../components/layout/AppShell';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { SkeletonGrid } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { IconUser, IconArrowRight } from '../components/ui/icons';
import { creators, API_BASE } from '../lib/api';
import { apiErrorMessage } from '../lib/errors';
import { track } from '../lib/analytics';
import type { CreatorSort, CreatorSummary } from '../lib/types';

/**
 * Creator directory (F-054, Wingspan cut 11) — the public front door: browse
 * every creator with a published catalog. Each card links to that creator's
 * storefront (/u/:id, cut 9). FINAL_STATE §7 public catalog surfaces.
 */
function Monogram({ name }: { name: string }) {
  const letter = (name.trim()[0] ?? '?').toUpperCase();
  return (
    <div
      className="flex h-12 w-12 shrink-0 items-center justify-center bg-surface-3 type-heading-2 text-secondary"
      aria-hidden
    >
      {letter}
    </div>
  );
}

function CreatorCard({ creator }: { creator: CreatorSummary }) {
  const { t } = useI18n();
  return (
    <Card interactive className="flex flex-col gap-3 p-4">
      <Link to={`/u/${creator.id}`} className="focus-ring flex items-center gap-3 rounded-sm">
        {creator.avatar_url ? (
          <img
            src={`${API_BASE}${creator.avatar_url}`}
            alt=""
            className="h-12 w-12 shrink-0 object-cover"
            loading="lazy"
          />
        ) : (
          <Monogram name={creator.display_name} />
        )}
        <div className="min-w-0">
          <h3 className="type-heading-2 text-primary truncate" title={creator.display_name}>
            {creator.display_name}
          </h3>
          <p className="type-caption text-muted truncate">@{creator.handle}</p>
        </div>
      </Link>
      <div className="mt-auto flex items-center justify-between gap-2 pt-2">
        <span className="type-data text-secondary tnum">{t.directory.packsCount(creator.published_count)}</span>
        <Link
          to={`/u/${creator.id}`}
          className="focus-ring type-label text-accent inline-flex items-center gap-1 rounded-sm"
        >
          {t.directory.viewStorefront}
          <IconArrowRight size={16} />
        </Link>
      </div>
    </Card>
  );
}

export function CreatorDirectory() {
  const { t } = useI18n();
  const [sort, setSort] = useState<CreatorSort>('recent');
  const [items, setItems] = useState<CreatorSummary[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(
    async (nextPage: number, append: boolean) => {
      try {
        const res = await creators.directory({ sort, page: nextPage, limit: 24 });
        setItems((prev) => (append ? [...prev, ...res.items] : res.items));
        setPage(res.page);
        setHasMore(res.has_more);
        setError(null);
      } catch (err) {
        setError(apiErrorMessage(err, t));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [sort, t],
  );

  useEffect(() => {
    track('directory_viewed', { sort });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoading(true);
    void run(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort]);

  const SORTS: { key: CreatorSort; label: string }[] = [
    { key: 'recent', label: t.directory.sortRecent },
    { key: 'active', label: t.directory.sortActive },
    { key: 'alphabetical', label: t.directory.sortAlphabetical },
  ];

  const chip = (active: boolean) =>
    [
      'type-label rounded-sm border px-3 py-1.5 transition-colors duration-fast ease-machine focus-ring',
      active ? 'border-accent bg-accent-tint text-accent' : 'border-border-strong text-secondary hover:text-primary',
    ].join(' ');

  return (
    <div>
      <PageHeader title={t.directory.title} subtitle={t.directory.subtitle} />

      <div className="mb-5 flex flex-wrap items-center gap-2">
        {SORTS.map((s) => (
          <button key={s.key} type="button" className={chip(sort === s.key)} onClick={() => setSort(s.key)}>
            {s.label}
          </button>
        ))}
        {!loading ? <span className="type-data text-muted tnum ml-auto">{t.directory.resultCount(items.length)}</span> : null}
      </div>

      {error ? (
        <p className="type-body-sm text-danger mb-4" role="alert">
          {error}
        </p>
      ) : null}

      {loading ? (
        <SkeletonGrid count={8} />
      ) : items.length === 0 ? (
        <EmptyState icon={<IconUser size={48} />} title={t.directory.emptyTitle} body={t.directory.emptyBody} />
      ) : (
        <>
          <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(240px,1fr))]" aria-live="polite">
            {items.map((c) => (
              <CreatorCard key={c.id} creator={c} />
            ))}
          </div>
          {hasMore ? (
            <div className="mt-6 flex justify-center">
              <Button
                variant="ghost"
                size="md"
                loading={loadingMore}
                onClick={() => {
                  setLoadingMore(true);
                  void run(page + 1, true);
                }}
              >
                {t.common.loadingMore}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
